"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Loader2 } from "lucide-react";

import useWebRTCAudioSession, { VoiceType, FunctionCallEvent, ToolConfig } from "@/hooks/use-webrtc";
import useShowcanWs from "@/hooks/use-showcan-ws";
import type { ShowcanConversation } from "@/hooks/use-showcan-ws";
import { usePublicVoiceAgentQuery } from "@/hooks/use-public-voice-agent";
import { listPublicClinics } from "@/lib/api/generated/public-clinics/public-clinics";
import { customClient } from "@/lib/api/custom-client";
import type { Clinic, ListClinicsResponse } from "@/lib/api/model";

import FloatingButton from "./voice-agent/FloatingButton";
import ModeToggle from "./voice-agent/ModeToggle";
import VoiceVisualizer from "./voice-agent/VoiceVisualizer";
import VoiceFooter from "./voice-agent/VoiceFooter";
import ChatMessages from "./voice-agent/ChatMessages";
import ChatInput from "./voice-agent/ChatInput";
import ItemCards from "./voice-agent/ItemCards";
import ConversationLogPanel from "./voice-agent/ConversationLogPanel";

async function searchPublicClinics(
  agentId: string,
  params: { query: string },
  options?: RequestInit,
): Promise<ListClinicsResponse> {
  const normalizedParams = new URLSearchParams();
  normalizedParams.append("query", params.query);
  return customClient<ListClinicsResponse>(
    `/api/v1/public/voice-agents/${agentId}/clinics/search?${normalizedParams.toString()}`,
    {
      ...options,
      method: "GET",
    },
  );
}

export type EmbeddingVoiceAgentProps = {
  agentId: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onMuteChange?: (isMuted: boolean) => void;
  onMessageClick?: () => void;
};

export default function EmbeddingVoiceAgent({
  agentId,
  onConnect,
  onDisconnect,
  onMessageClick,
}: EmbeddingVoiceAgentProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<"connecting" | "connected" | "disconnected">("disconnected");
  const [mode, setMode] = useState<"voice" | "chat">("voice");
  const [chatInput, setChatInput] = useState("");
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [showClinics, setShowClinics] = useState(false);
  const [showLogPanel, setShowLogPanel] = useState(false);
  const pendingFunctionCallRef = useRef<FunctionCallEvent | null>(null);

  const { data: voiceAgent, isLoading: isLoadingAgent, error: agentError } = usePublicVoiceAgentQuery(agentId);

  const agent = {
    name: voiceAgent?.name ?? "AI Agent",
    label: "AI Agent",
    avatarUrl: voiceAgent?.avatarUrl ?? "https://api.dicebear.com/7.x/avataaars/svg?seed=Felix",
  };
  const tenantId = voiceAgent?.tenantId ?? "";
  const systemPrompt = voiceAgent?.systemPrompt ?? "You are a helpful assistant. Respond naturally in the user's language.";
  const voice = (voiceAgent?.voice as VoiceType) ?? "marin";
  const toolsConfig = (voiceAgent?.toolsConfig as ToolConfig[] | undefined) ?? undefined;
  const connectionType = (voiceAgent?.connectionType as string) ?? "openai-realtime";
  const isShowcanSts = connectionType === "showcan-sts";

  // --- OpenAI Realtime (WebRTC) hook ---
  const webrtc = useWebRTCAudioSession(voice, tenantId, systemPrompt, agentId, toolsConfig, async (event: FunctionCallEvent) => {
    if (isShowcanSts) return; // Not used for showcan-sts
    if (event.name === 'search_products') {
      const query = event.arguments?.query as string || '';
      if (query) {
        try {
          const response = await searchPublicClinics(agentId, { query });
          const clinicList = response.clinics ?? [];
          setClinics(clinicList);
          setShowClinics(true);
          pendingFunctionCallRef.current = event;
        } catch (error) {
          console.error("Failed to search clinics:", error);
        }
      }
    } else {
      const matchedTool = toolsConfig?.find(tool => tool.name === event.name);
      if (matchedTool) {
        try {
          const response = await listPublicClinics(agentId, { toolName: event.name });
          const clinicList = response.clinics ?? [];
          setClinics(clinicList);
          setShowClinics(true);
          pendingFunctionCallRef.current = event;
        } catch (error) {
          console.error("Failed to fetch clinics:", error);
        }
      }
    }
  });

  // --- Showcan STS (WebSocket) hook ---
  const showcan = useShowcanWs();

  useEffect(() => {
    if (!isShowcanSts && pendingFunctionCallRef.current && clinics.length > 0 && showClinics) {
      const event = pendingFunctionCallRef.current;
      const clinicNames = clinics.map((c) => c.name).join("、");

      if (event.name === 'search_products') {
        const query = event.arguments?.query as string || '';
        webrtc.sendFunctionCallOutput(
          event.callId,
          `検索結果を画面に表示しました。「${query}」に関連する商品: ${clinicNames}。ユーザーに検索結果を案内してください。`,
        );
      } else {
        const template = voiceAgent?.functionCallTemplate
          || "以下の商品があります: {item_names}。気になる商品をタップしてください。";
        const output = template.replace("{item_names}", clinicNames);
        webrtc.sendFunctionCallOutput(
          event.callId,
          `商品一覧を画面に表示しました。${output}`,
        );
      }

      pendingFunctionCallRef.current = null;
    }
  }, [clinics, showClinics, webrtc.sendFunctionCallOutput, isShowcanSts]);

  const handleClinicClick = useCallback((item: { name: string; url: string; image?: string }) => {
    window.open(item.url, "_blank");
    if (!isShowcanSts) {
      webrtc.interruptAndSendMessage(`「${item.name}」について詳しく教えてください。`);
    } else {
      showcan.sendTextMessage(`「${item.name}」について詳しく教えてください。`);
    }
  }, [isShowcanSts, webrtc.interruptAndSendMessage, showcan.sendTextMessage]);

  const handleOpen = useCallback(async () => {
    setIsOpen(true);
    setConnectionStatus("connecting");
    try {
      if (isShowcanSts) {
        showcan.connect();
        // showcan.connect is async in the sense that status updates via state
        // We'll track status via the showcan.status effect below
      } else {
        const currentPageUrl = window.location.pathname;
        await webrtc.startSession(currentPageUrl);
        setConnectionStatus("connected");
      }
      onConnect?.();
    } catch (error) {
      console.error("Failed to start session:", error);
      setConnectionStatus("disconnected");
    }
  }, [isShowcanSts, webrtc.startSession, showcan.connect, onConnect]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setConnectionStatus("disconnected");
    setClinics([]);
    setShowClinics(false);
    setMode("voice");
    setChatInput("");
    setShowLogPanel(false);
    pendingFunctionCallRef.current = null;
    if (isShowcanSts) {
      if (showcan.isRecording) showcan.stopRecording();
      showcan.disconnect();
    } else {
      webrtc.stopSession();
    }
    onDisconnect?.();
  }, [isShowcanSts, webrtc.stopSession, showcan.disconnect, showcan.stopRecording, showcan.isRecording, onDisconnect]);

  const handleModeChange = useCallback((newMode: "voice" | "chat") => {
    setMode(newMode);
    if (!isShowcanSts) {
      webrtc.setTextOnlyMode(newMode === "chat");
    }
    if (newMode === "chat") {
      onMessageClick?.();
    }
  }, [isShowcanSts, webrtc.setTextOnlyMode, onMessageClick]);

  const handleSendChatMessage = useCallback(() => {
    const message = chatInput.trim();
    if (!message) return;
    if (isShowcanSts) {
      showcan.sendTextMessage(message);
    } else {
      webrtc.sendTextMessage(message);
    }
    setChatInput("");
  }, [chatInput, isShowcanSts, showcan.sendTextMessage, webrtc.sendTextMessage]);

  // Track WebRTC session status
  useEffect(() => {
    if (!isShowcanSts && webrtc.isSessionActive && connectionStatus === "connecting") {
      setConnectionStatus("connected");
    }
  }, [isShowcanSts, webrtc.isSessionActive, connectionStatus]);

  // Track Showcan STS status
  useEffect(() => {
    if (!isShowcanSts) return;
    if (showcan.status === "ready") {
      setConnectionStatus("connected");
      // Recording is auto-started inside connect() when "ready" arrives
    } else if (showcan.status === "error" || showcan.status === "disconnected") {
      if (isOpen) {
        setConnectionStatus("disconnected");
      }
    }
  }, [isShowcanSts, showcan.status, isOpen]);

  // Build unified conversation for rendering
  const conversation = (() => {
    if (!isShowcanSts) return webrtc.conversation;
    const msgs = showcan.conversation.map((c: ShowcanConversation) => ({
      role: c.role,
      text: c.text,
      timestamp: c.timestamp,
      isFinal: true,
    }));
    if (showcan.currentTranscript) {
      msgs.push({
        role: "user" as const,
        text: showcan.currentTranscript,
        timestamp: new Date().toISOString(),
        isFinal: false,
      });
    }
    if (showcan.currentLlmText) {
      msgs.push({
        role: "assistant" as const,
        text: showcan.currentLlmText,
        timestamp: new Date().toISOString(),
        isFinal: false,
      });
    }
    return msgs;
  })();

  const clinicItems = clinics.map((c) => ({
    name: c.name,
    url: c.url,
    image: c.image,
  }));

  if (isLoadingAgent) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <div className="flex items-center gap-3 bg-white text-slate-700 pl-4 pr-6 py-3 rounded-full shadow-lg">
          <Loader2 className="w-5 h-5 animate-spin text-[#e99bad]" />
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  if (agentError || !voiceAgent) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <div className="flex items-center gap-3 bg-red-50 text-red-600 pl-4 pr-6 py-3 rounded-full shadow-lg border border-red-200">
          <span className="text-sm">Agent not found</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <FloatingButton
        agentName={agent.name}
        agentLabel={agent.label}
        avatarUrl={agent.avatarUrl}
        isOpen={isOpen}
        onClick={handleOpen}
      />

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center md:justify-end md:items-end p-0 md:p-6 pointer-events-none">
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-[2px] pointer-events-auto"
            onClick={handleClose}
          />

          <div className="pointer-events-auto relative w-full h-full md:w-[400px] md:h-[650px] bg-white md:rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-10 fade-in duration-300">
            {connectionStatus === "connecting" && (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
                <Loader2 className="w-8 h-8 animate-spin text-[#e99bad] mb-3" />
                <span className="text-sm text-slate-500">接続中...</span>
              </div>
            )}

            <ModeToggle mode={mode} onModeChange={handleModeChange} />

            {mode === "voice" ? (
              <>
                <VoiceVisualizer
                  isActive={connectionStatus === "connected"}
                  agentName={agent.name}
                  avatarUrl={agent.avatarUrl}
                />

                {showClinics && clinicItems.length > 0 && (
                  <div className="px-4 pb-4">
                    <ItemCards
                      items={clinicItems}
                      onItemClick={handleClinicClick}
                      variant="voice"
                    />
                  </div>
                )}

                <VoiceFooter
                  onShowLog={() => setShowLogPanel(true)}
                  onEndSession={handleClose}
                />
              </>
            ) : (
              <>
                <ChatMessages
                  conversation={conversation}
                  agentName={agent.name}
                  avatarUrl={agent.avatarUrl}
                />

                {showClinics && clinicItems.length > 0 && (
                  <div className="px-4 pb-2">
                    <ItemCards
                      items={clinicItems}
                      onItemClick={handleClinicClick}
                      variant="chat"
                    />
                  </div>
                )}

                <ChatInput
                  value={chatInput}
                  onChange={setChatInput}
                  onSend={handleSendChatMessage}
                />
              </>
            )}

            <ConversationLogPanel
              isOpen={showLogPanel}
              onClose={() => setShowLogPanel(false)}
              conversation={conversation}
              agentName={agent.name}
            />
          </div>
        </div>
      )}
    </>
  );
}
